import React, { useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Paper,
  Chip,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getPersonaCidi } from '../../../services/asistenciaLaboral.service';
import BurbujasLoader from '../../../components/UIElements/BurbujasLoader';

const parseTimeToDayjs = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = timeStr.trim().split(':');
  if (parts.length >= 2) {
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
    }
  }
  return null;
};

export default function ModalAsistenciaManual({ open, onClose, onSave }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [cuil, setCuil] = useState('');
  const [fecha, setFecha] = useState(dayjs().format('YYYY-MM-DD'));
  const [hora, setHora] = useState(dayjs().format('HH:mm'));
  const [tipoRegistro, setTipoRegistro] = useState('ENTRADA');
  const [sede, setSede] = useState('Sede Central');
  const [observacion, setObservacion] = useState('');

  const [loadingCiDi, setLoadingCiDi] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cidiData, setCidiData] = useState(null);
  const [error, setError] = useState('');

  const handleReset = () => {
    setCuil('');
    setFecha(dayjs().format('YYYY-MM-DD'));
    setHora(dayjs().format('HH:mm'));
    setTipoRegistro('ENTRADA');
    setSede('Sede Central');
    setObservacion('');
    setCidiData(null);
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSearchCiDi = async () => {
    const clean = cuil.replace(/\D/g, '');
    if (clean.length !== 11) {
      setError('El CUIL debe tener 11 dígitos numéricos.');
      return;
    }

    setLoadingCiDi(true);
    setError('');
    setCidiData(null);
    try {
      const data = await getPersonaCidi(clean);
      setCidiData(data);
    } catch (err) {
      setError(err.message || 'No se encontró la persona en CiDi.');
    } finally {
      setLoadingCiDi(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clean = cuil.replace(/\D/g, '');
    if (clean.length !== 11) {
      setError('El CUIL debe tener 11 dígitos.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSave({
        cuil: clean,
        fecha,
        hora,
        tipo_registro: tipoRegistro,
        sede,
        observacion,
      });
      handleClose();
    } catch (err) {
      setError(err.message || 'Error al registrar asistencia manual.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
        },
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <DialogTitle
        sx={{
          bgcolor: 'secondary.main',
          color: 'common.white',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'common.white' }}>
          Registro Manual de Asistencia
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'common.white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Stack spacing={2.5}>
            {/* Input de CUIL con botón de búsqueda */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                CUIL del Trabajador *
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  placeholder="20123456789"
                  fullWidth
                  size="small"
                  value={cuil}
                  onChange={(e) => setCuil(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: '10px', bgcolor: 'common.white' },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSearchCiDi}
                  disabled={loadingCiDi || cuil.length !== 11}
                  sx={{
                    fontWeight: 600,
                    borderRadius: '10px',
                    bgcolor: 'primary.main',
                    px: 2.5,
                  }}
                >
                  {loadingCiDi ? <BurbujasLoader small /> : <SearchIcon />}
                </Button>
              </Stack>
            </Box>

            {/* Tarjeta de Trabajador verificado */}
            {cidiData && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(50, 164, 48, 0.08)',
                  border: '1px solid',
                  borderColor: 'success.main',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                      PERSONA VERIFICADA EN CIDI
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                      {cidiData.nombre} {cidiData.apellido}
                    </Typography>
                  </Box>
                  <Chip label="CiDi OK" color="success" size="small" icon={<CheckCircleIcon />} />
                </Stack>
              </Paper>
            )}

            {/* Tipo de Registro */}
            <TextField
              select
              label="Tipo de Registro"
              size="small"
              fullWidth
              value={tipoRegistro}
              onChange={(e) => setTipoRegistro(e.target.value)}
              sx={{ bgcolor: 'common.white', borderRadius: '10px' }}
            >
              <MenuItem value="ENTRADA">🟢 ENTRADA (Ingreso)</MenuItem>
              <MenuItem value="SALIDA">🟠 SALIDA (Egreso)</MenuItem>
            </TextField>

            {/* Fecha y Hora */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                type="date"
                label="Fecha"
                size="small"
                fullWidth
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: 'common.white', borderRadius: '10px' }}
              />
              <TimePicker
                label="Hora"
                ampm={false}
                format="HH:mm"
                value={parseTimeToDayjs(hora)}
                onChange={(newValue) => {
                  if (!newValue) {
                    setHora('');
                  } else if (newValue.isValid && newValue.isValid()) {
                    setHora(newValue.format('HH:mm'));
                  }
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "HH:mm",
                    InputLabelProps: { shrink: true },
                    sx: { bgcolor: 'common.white', borderRadius: '10px' },
                  }
                }}
              />
            </Stack>

            {/* Sede */}
            <TextField
              label="Sede / Dependencia"
              size="small"
              fullWidth
              value={sede}
              onChange={(e) => setSede(e.target.value)}
              sx={{ bgcolor: 'common.white', borderRadius: '10px' }}
            />

            {/* Observación */}
            <TextField
              label="Observación (Opcional)"
              size="small"
              multiline
              rows={2}
              fullWidth
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Ej: Registro justificado por problema técnico en el lector..."
              sx={{ bgcolor: 'common.white', borderRadius: '10px' }}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          bgcolor: 'common.white',
          borderTop: '1px solid',
          borderColor: 'grey.300',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1,
        }}
      >
        <Button
          variant="text"
          fullWidth={isMobile}
          onClick={handleClose}
          sx={{ fontWeight: 600, color: 'text.secondary' }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          fullWidth={isMobile}
          onClick={handleSubmit}
          disabled={saving || cuil.length !== 11}
          startIcon={<SaveIcon />}
          sx={{
            fontWeight: 600,
            borderRadius: '12px',
            bgcolor: 'success.main',
            color: 'common.white',
            '&:hover': { bgcolor: '#288826' },
          }}
        >
          {saving ? 'Guardando...' : 'Guardar Asistencia'}
        </Button>
      </DialogActions>
      </LocalizationProvider>
    </Dialog>
  );
}
